import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useClient } from 'urql';
import { fromPromise } from 'xstate5';
import { useNoticeBannersStackContext } from '@sg/garnish';
import { type Order } from '@sg/graphql-schema';

import {
  type PartialLineItem,
  type UpdateLineItemParams,
  type UpdateLineItemResponse,
} from '@order/features/ordering';

import {
  RemoveLineItemDocument,
  type RemoveLineItemMutation,
  type RemoveLineItemMutationVariables,
  UpdateLineItemDocument,
  type UpdateLineItemMutation,
  type UpdateLineItemMutationVariables,
} from '../../GraphQL/BagCart.generated';
import { prepareCart } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook for updating the line item in the cart.
 * If a {quantity} of 0 is passed, it will call the remove mutation.
 * Otherwise it will call the update mutation for either quantity or custom name.
 */
export const useUpdateLineItem = () => {
  const client = useClient();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();

  // ─── Mutation ─────────────────────────────────────────────────────────────

  const updateLineItemMutation = useCallback(
    async (lineItem: PartialLineItem, params: UpdateLineItemParams) => {
      const updateMutation = client.mutation<
        UpdateLineItemMutation,
        UpdateLineItemMutationVariables
      >;

      const removeMutation = client.mutation<
        RemoveLineItemMutation,
        RemoveLineItemMutationVariables
      >;

      if (params.quantity === 0) {
        const response = await removeMutation(
          RemoveLineItemDocument,
          {
            input: {
              lineItemId: params.lineItemId,
            },
          },
          {
            requestPolicy: 'network-only',
          },
        ).toPromise();

        return response.data?.removeFromCart;
      }

      const response = await updateMutation(
        UpdateLineItemDocument,
        {
          input: {
            lineItemId: params.lineItemId,
            productId: params.productId,
            quantity: params.quantity,
            customName: lineItem.customName,
            mixedDressingDetails: lineItem.mixedDressingDetails ?? [],
            additions: lineItem.addedIngredients?.map(getIngredientId) ?? [],
            removals: lineItem.removedIngredients?.map(getIngredientId) ?? [],
            substitutions: [],
          },
        },
        {
          requestPolicy: 'network-only',
        },
      ).toPromise();

      return response.data?.editLineItemInCart;
    },
    [client.mutation],
  );
  // ─── Helpers ──────────────────────────────────────────────────────────────

  const updateLineItem = useMemo(
    () =>
      fromPromise<UpdateLineItemResponse, UpdateLineItemParams>(
        async ({ input }) => {
          const { lineItemId, lineItems } = input;
          const lineItem = lineItems.find(({ id }) => id === lineItemId);

          if (!lineItem) return;

          const response = await updateLineItemMutation(lineItem, input);

          if (
            response?.__typename === 'EditLineItemInCartSuccess' ||
            response?.__typename === 'RemoveFromCartSuccess'
          ) {
            return prepareCart(response.cart as Partial<Order>);
          }

          const defaultError = formatMessage(messages.defaultError);

          if (!response) {
            addNoticeBanner({ text: defaultError, palette: 'caution' });
            return;
          }

          if (response.__typename === 'ValidationError') {
            addNoticeBanner({
              text: response?.message ?? response?.fieldErrors?.[0].message,
              palette: 'caution',
            });
            return;
          }

          if ('message' in response) {
            addNoticeBanner({ text: response.message, palette: 'caution' });
            return;
          }

          addNoticeBanner({ text: defaultError, palette: 'caution' });
        },
      ),
    [addNoticeBanner, formatMessage, updateLineItemMutation],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return { updateLineItem };
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const getIngredientId = (ingredient?: { id?: string }) => {
  return { ingredientId: ingredient?.id ?? '' };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  defaultError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Bag | Update Line Item | Default Error',
  },
});
